import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Card, CardContent } from '../../../components/ui/card';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Trash2, Edit, X, Loader2, Archive, Disc, Zap, MoveRight } from 'lucide-react';
import { MovePipesDialog } from './MovePipesDialog';
import { movePipesToPipeSize } from '../api/movePipesToPipeSize';
import type { DashboardProject } from '../../project/types/project';
import {
  PIPEFITTER_GET_CONFIGURATION,
  PIPEFITTER_FETCH_PIPE_STATUS,
  PIPEFITTER_ASSIGN_PIPE_STATUS_DATA,
} from '../../../redux/Pipefitter/types';
import {
  pipefitterUpdatePipeStatus,
  pipefitterSetPipeStatusPagination,
} from '../../../redux/Pipefitter/actions';
import { configurationConstants } from '../constants/configurationConstants';
import { PipeDataView } from './PipeDataView';
import { usePipeFilterColumns } from '../helpers/pipeFilterColumns';
import { countPipeEndPresence, usePipeStatusFilters } from '../helpers/usePipeStatusFilters';
import { mapPipeStatusToTableRows } from '../helpers/mapPipeStatusTableRows';
import { commonConstants } from '../constants/commonConstants';
import { PipeStatusFilters } from './PipeStatusFilters';
import PipeFfitter from '../../project/components/dashboard/materials/PipeFfitter';
import { Pagination } from '../../../components/ui/Pagination';
import { UpdatePipeIdDialog } from './UpdatePipeIdDialog';
import { UpdatePipeFlagsDialog, type UpdatePipeFlagsDialogPipe } from './UpdatePipeFlagsDialog';
import { deletePipeEnds, type PipeEndKey } from '../../../services/pipeIdEditApi';
import { logPipeEndsDeleted, logPipesMovedToPipeSize } from '../utils/pipefitterAudit';
import { Notification as customNotification } from '../../../components/ui/custom-notification';

export interface PipeDetailsSelectionItem {
  id: string;
  pipeID: string;
  sequence?: string;
  status?: string;
}

interface PipeFilterProps {
  project: DashboardProject;
  canEdit?: boolean;
  canReport?: boolean;
  selectedPipe?: any;
  onSelectionChange?: (details: PipeDetailsSelectionItem[]) => void;
  /** Project pipe sizes (from ProjectPipefitter) — targets for "Move to pipe size". */
  pipeSizesData?: any[];
}

interface PipeStatusData {
  pipeID: string;
  projectNumber: string;
  sequence: string;
  status: string;
  endA: boolean;
  endB: boolean;
}

const PIPE_STATUS_OPTIONS = ['Default State', 'Available', 'Used', 'Discarded'] as const;

/** UI table page size only (client-side). Network fetch uses saga batches of 500. */
const PIPE_STATUS_MAX_PAGE_SIZE = 500;

function clampPipeStatusPageSize(n: number): number {
  return Math.min(PIPE_STATUS_MAX_PAGE_SIZE, Math.max(1, Math.floor(n)));
}

function pipeEndPresenceFlagSuffix(
  flangeCount: number,
  archiveCount: number,
  anodeCount: number
): React.ReactNode {
  if (flangeCount <= 0 && archiveCount <= 0 && anodeCount <= 0) return null;
  const parts: string[] = [];
  if (archiveCount > 0) parts.push(`Archived: ${archiveCount}`);
  if (flangeCount > 0) parts.push(`Flange: ${flangeCount}`);
  if (anodeCount > 0) parts.push(`Anode: ${anodeCount}`);
  return <> ({parts.join(', ')})</>;
}

export const PipeFilter: React.FC<PipeFilterProps> = ({
  project,
  canEdit = true,
  canReport,
  selectedPipe,
  onSelectionChange,
  pipeSizesData = [],
}) => {
  const dispatch = useDispatch();
  const projectNumber = project?.project?.projectNumber;
  const configurationList = useSelector((state: any) => state.pipefitterConfiguration?.configurationList || []);
  const pipeStatusDataRaw = useSelector((state: any) => state.pipefitterReports?.pipeStatusData);
  // Ensure pipeStatusData is always an array (handle case where API returns object)
  const pipeStatusData = Array.isArray(pipeStatusDataRaw) ? pipeStatusDataRaw : (pipeStatusDataRaw ? [pipeStatusDataRaw] : []);
  const isReportsLoading = useSelector((state: any) => state.pipefitterReports?.isReportsLoading || false);
  const pipeStatusLoadingMore = useSelector((state: any) => state.pipefitterReports?.pipeStatusLoadingMore || false);
  const pipeStatusFetchComplete = useSelector((state: any) => state.pipefitterReports?.pipeStatusFetchComplete || false);
  const pipeStatusTotalCount = useSelector((state: any) => {
    const n = Number(state.pipefitterReports?.pipeStatusTotalCount);
    return Number.isFinite(n) ? n : null;
  });
  const pipeStatusSkip = useSelector((state: any) => state.pipefitterReports?.pipeStatusSkip ?? 0);
  const pipeStatusLimit = useSelector((state: any) => state.pipefitterReports?.pipeStatusLimit ?? 25);

  /** Keeps latest page size for Pagination callbacks (that component calls onPageChange(1) after onItemsPerPageChange). */
  const pipeStatusLimitRef = useRef(pipeStatusLimit);
  pipeStatusLimitRef.current = pipeStatusLimit;

  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [isTableModalVisible, setIsTableModalVisible] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pipesPendingDelete, setPipesPendingDelete] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pipeToEdit, setPipeToEdit] = useState<any | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editSequence, setEditSequence] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isPipeFitterOpen, setIsPipeFitterOpen] = useState(false);
  const [pipeFitterRow, setPipeFitterRow] = useState<any>(null);
  const [pipeFitterDefaultEnd, setPipeFitterDefaultEnd] = useState<'EndA' | 'EndB'>('EndA');
  const [pipeIdEditRow, setPipeIdEditRow] = useState<any | null>(null);
  const [isPipeIdEditOpen, setIsPipeIdEditOpen] = useState(false);
  const [archiveEditPipes, setArchiveEditPipes] = useState<UpdatePipeFlagsDialogPipe[]>([]);
  const [isPipeArchiveEditOpen, setIsPipeArchiveEditOpen] = useState(false);
  const [flangeEditPipes, setFlangeEditPipes] = useState<UpdatePipeFlagsDialogPipe[]>([]);
  const [isPipeFlangeEditOpen, setIsPipeFlangeEditOpen] = useState(false);
  const [anodeEditPipes, setAnodeEditPipes] = useState<UpdatePipeFlagsDialogPipe[]>([]);
  const [isPipeAnodeEditOpen, setIsPipeAnodeEditOpen] = useState(false);
  /** Default off: hide archived pipes, Archive action, and Archive count. */
  const [includeArchivedPipes, setIncludeArchivedPipes] = useState(false);
  const [deleteEndA, setDeleteEndA] = useState(true);
  const [deleteEndB, setDeleteEndB] = useState(true);
  const [moveTargetName, setMoveTargetName] = useState('');
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  /** 1-based page from client-side pagination state (Redux). */
  const clientPipeStatusPage = useMemo(() => {
    const lim = Math.max(1, pipeStatusLimit);
    return Math.floor(pipeStatusSkip / lim) + 1;
  }, [pipeStatusSkip, pipeStatusLimit]);

  const refetchPipeStatus = useCallback(() => {
    const projectId = project?.project?.projectId;
    const isAllPipeSizes = Boolean(selectedPipe?.isAllPipeSizes);
    const pipeId = selectedPipe?.pipeId;
    if (projectId == null && projectNumber == null) return;
    if (!isAllPipeSizes && (pipeId == null || pipeId === '')) return;
    dispatch({
      type: PIPEFITTER_FETCH_PIPE_STATUS,
      payload: {
        project_id: projectId,
        ...(isAllPipeSizes ? { allPipeSizes: true } : { pipe_id: pipeId }),
        project_num: projectNumber ?? undefined,
        project_name: project?.project?.projectName ?? '',
        limit: pipeStatusLimitRef.current,
      },
    });
  }, [
    dispatch,
    project?.project?.projectId,
    project?.project?.projectName,
    projectNumber,
    selectedPipe?.pipeId,
    selectedPipe?.isAllPipeSizes,
  ]);

  /** Client-side table pagination only — does not trigger API fetch. */
  const setClientPagination = useCallback(
    (skip: number, limit: number) => {
      dispatch(pipefitterSetPipeStatusPagination({
        skip: Math.max(0, skip),
        limit: clampPipeStatusPageSize(limit),
      }));
    },
    [dispatch]
  );

  // Load full pipe status list when project and pipe are selected (batched in saga).
  // All pipe sizes → omit pipe_id so API returns every group / pipe for the project.
  useEffect(() => {
    const projectId = project?.project?.projectId;
    const isAllPipeSizes = Boolean(selectedPipe?.isAllPipeSizes);
    const pipeId = selectedPipe?.pipeId;
    setSelectedRowKeys([]);
    if (projectId == null && projectNumber == null) return;
    if (!selectedPipe) return;
    if (!isAllPipeSizes && (pipeId == null || pipeId === '')) return;
    refetchPipeStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    projectNumber,
    project?.project?.projectId,
    project?.project?.projectName,
    selectedPipe?.pipeId,
    selectedPipe?.isAllPipeSizes,
    refetchPipeStatus,
  ]);

  const allTableRows = useMemo(
    () => mapPipeStatusToTableRows(pipeStatusData as Record<string, unknown>[]),
    [pipeStatusData]
  );

  const {
    searchQuery,
    setSearchQuery,
    filters,
    onFilterChange,
    onEndPresenceChange,
    onArchiveChange,
    onPipeMarkChange,
    onDuplicatesChange,
    uniquePipeIDs,
    uniqueGroupIds,
    hasActiveFilters,
    onResetFilters,
    filteredData: filteredRowsRaw,
  } = usePipeStatusFilters({ data: allTableRows });

  const isAllPipeSizes = Boolean(selectedPipe?.isAllPipeSizes);

  // Clear group filter when leaving All pipe sizes (column/filter hidden).
  useEffect(() => {
    if (!isAllPipeSizes && filters.groupId.length > 0) {
      onFilterChange('groupId')([]);
    }
  }, [isAllPipeSizes, filters.groupId.length, onFilterChange]);

  // Archive dropdown already filters in usePipeStatusFilters.
  // "Include archived" only applies when Archive filter is "all" — otherwise
  // Archive=Yes would always end empty (archived rows stripped again here).
  const filteredRows = useMemo(() => {
    if (filters.archive === 'yes' || filters.archive === 'no') {
      return filteredRowsRaw;
    }
    return includeArchivedPipes
      ? filteredRowsRaw
      : filteredRowsRaw.filter((row) => !row.isArchive);
  }, [filteredRowsRaw, includeArchivedPipes, filters.archive]);

  const endPresenceCounts = useMemo(() => countPipeEndPresence(filteredRows), [filteredRows]);

  const endPresenceFlagCounts = useMemo(() => {
    let bothFlange = 0;
    let bothArchive = 0;
    let bothAnode = 0;
    let endAOnlyFlange = 0;
    let endAOnlyArchive = 0;
    let endAOnlyAnode = 0;
    let endBOnlyFlange = 0;
    let endBOnlyArchive = 0;
    let endBOnlyAnode = 0;

    for (const row of filteredRows) {
      if (row.endA && row.endB) {
        if (row.isFlange) bothFlange += 1;
        if (row.isArchive) bothArchive += 1;
        if (row.isAnode) bothAnode += 1;
      } else if (row.endA) {
        if (row.isFlange) endAOnlyFlange += 1;
        if (row.isArchive) endAOnlyArchive += 1;
        if (row.isAnode) endAOnlyAnode += 1;
      } else if (row.endB) {
        if (row.isFlange) endBOnlyFlange += 1;
        if (row.isArchive) endBOnlyArchive += 1;
        if (row.isAnode) endBOnlyAnode += 1;
      }
    }

    return {
      bothFlange,
      bothArchive,
      bothAnode,
      endAOnlyFlange,
      endAOnlyArchive,
      endAOnlyAnode,
      endBOnlyFlange,
      endBOnlyArchive,
      endBOnlyAnode,
    };
  }, [filteredRows]);

  const resetClientPage = useCallback(() => {
    setClientPagination(0, pipeStatusLimitRef.current);
  }, [setClientPagination]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      resetClientPage();
    },
    [setSearchQuery, resetClientPage]
  );

  const handleResetFilters = useCallback(() => {
    onResetFilters();
    resetClientPage();
  }, [onResetFilters, resetClientPage]);

  const handleEndPresenceChange = useCallback(
    (value: Parameters<typeof onEndPresenceChange>[0]) => {
      onEndPresenceChange(value);
      resetClientPage();
    },
    [onEndPresenceChange, resetClientPage]
  );

  const handleArchiveChange = useCallback(
    (value: Parameters<typeof onArchiveChange>[0]) => {
      onArchiveChange(value);
      // Selecting "Archived" must include archived pipes in the table.
      if (value === 'yes') setIncludeArchivedPipes(true);
      resetClientPage();
    },
    [onArchiveChange, resetClientPage]
  );

  const handlePipeMarkChange = useCallback(
    (value: Parameters<typeof onPipeMarkChange>[0]) => {
      onPipeMarkChange(value);
      resetClientPage();
    },
    [onPipeMarkChange, resetClientPage]
  );

  const handleDuplicatesChange = useCallback(
    (value: Parameters<typeof onDuplicatesChange>[0]) => {
      onDuplicatesChange(value);
      resetClientPage();
    },
    [onDuplicatesChange, resetClientPage]
  );

  const handleIncludeArchivedChange = useCallback(
    (checked: boolean) => {
      setIncludeArchivedPipes(checked);
      resetClientPage();
    },
    [resetClientPage]
  );

  useEffect(() => {
    resetClientPage();
    // Reset to page 1 when column filters change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.pipeID,
    filters.sequence,
    filters.status,
    filters.endA,
    filters.endB,
    filters.endPresence,
    filters.archive,
    filters.pipeMark,
    filters.duplicates,
  ]);

  useEffect(() => {
    if (pipeStatusSkip > 0 && pipeStatusSkip >= filteredRows.length) {
      const lim = Math.max(1, pipeStatusLimit);
      const maxSkip = Math.max(0, filteredRows.length - lim);
      const alignedSkip = Math.floor(maxSkip / lim) * lim;
      setClientPagination(alignedSkip, lim);
    }
  }, [filteredRows.length, pipeStatusSkip, pipeStatusLimit, setClientPagination]);

  const displayData = useMemo(() => {
    const total = filteredRows.length;
    const paginate = total > commonConstants.clientPaginationThreshold;
    const start = paginate ? pipeStatusSkip : 0;
    const limit = paginate ? pipeStatusLimit : total;
    return filteredRows
      .slice(start, start + limit)
      .map((row, idx) => ({ ...row, slNo: start + idx + 1 }));
  }, [filteredRows, pipeStatusSkip, pipeStatusLimit]);

  const toolbarPipeCount = filteredRows.length;
  const handleEditRow = (row: any) => {
    setPipeToEdit(row);
    setEditStatus(row.status === 'defaultState' ? 'Default State' : (row.status ?? ''));
    setEditSequence(row.sequence ?? '');
    setIsEditModalOpen(true);
  };
  const handleEditPipeId = (row: any) => {
    setPipeIdEditRow(row);
    setIsPipeIdEditOpen(true);
  };
  const handleEditArchive = (row: any) => {
    setArchiveEditPipes([toFlagPipeDetail(row)]);
    setIsPipeArchiveEditOpen(true);
  };
  const handleEditFlange = (row: any) => {
    setFlangeEditPipes([toFlagPipeDetail(row)]);
    setIsPipeFlangeEditOpen(true);
  };
  const handleEditAnode = (row: any) => {
    setAnodeEditPipes([toFlagPipeDetail(row)]);
    setIsPipeAnodeEditOpen(true);
  };
  const toDeletePipeDetail = (r: any) => ({
    id: r.id,
    pipeID: r.pipeID,
    sequence: r.sequence,
    status: r.status,
    pipeData: r.pipeData,
    endA: r.endA,
    endB: r.endB,
    pipeEndIDA: r.pipeEndIDA,
    pipeEndIDB: r.pipeEndIDB,
  });

  const toFlagPipeDetail = (r: any): UpdatePipeFlagsDialogPipe => ({
    id: r.id,
    pipeID: r.pipeID,
    isArchive: r.isArchive,
    isFlange: r.isFlange,
    isAnode: r.isAnode,
  });

  const handleDeleteRow = (row: any) => {
    // Always target only the clicked row — ignore multi-checkbox selection.
    setPipesPendingDelete([toDeletePipeDetail(row)]);
    const hasA = Boolean(row?.endA || row?.pipeEndIDA);
    const hasB = Boolean(row?.endB || row?.pipeEndIDB);
    setDeleteEndA(hasA);
    setDeleteEndB(hasB);
    setDeleteConfirmOpen(true);
  };
  const handleOpenPipeFitter = (row: any, endType: 'EndA' | 'EndB' = 'EndA') => {
    setPipeFitterRow(row);
    setPipeFitterDefaultEnd(endType);
    setIsPipeFitterOpen(true);
  };

  const columns = usePipeFilterColumns({
    onEdit: handleEditRow,
    onEditPipeId: handleEditPipeId,
    onEditArchive: handleEditArchive,
    onEditFlange: handleEditFlange,
    onEditAnode: handleEditAnode,
    onDelete: handleDeleteRow,
    onOpenPipeFitter: handleOpenPipeFitter,
    canEdit,
    showArchiveAction: includeArchivedPipes,
    showGroupIdColumn: isAllPipeSizes,
  });

  const selectedPipeDetails = useMemo(
    () =>
      filteredRows
        .filter((row: any) => selectedRowKeys.some((key) => String(key) === String(row.id)))
        .map(toDeletePipeDetail),
    [filteredRows, selectedRowKeys]
  );

  useEffect(() => {
    onSelectionChange?.(selectedPipeDetails);
  }, [selectedPipeDetails, onSelectionChange]);

  /**
   * Targets for Move — only pipe sizes with the SAME pipe size AND same wall
   * thickness as the source (moving changes Group ID only; OD/wall never change).
   * Also excludes the pipe size the selected pipe(s) already belong to:
   * the current Pipe Size tab, and the shared group of the checked rows.
   */
  const moveTargetOptions = useMemo(() => {
    const selectedRows = filteredRows.filter((row: any) =>
      selectedRowKeys.some((key) => String(key) === String(row.id))
    );
    const selectedGroups = new Set(
      selectedRows.map((row: any) => String(row.groupId ?? '').trim()).filter(Boolean)
    );
    // Only exclude by row group when all checked pipes share one group.
    const sharedGroup =
      selectedRows.length > 0 && selectedGroups.size === 1
        ? Array.from(selectedGroups)[0]
        : null;
    const currentPipeId = selectedPipe?.pipeId;

    // Source size + wall: current tab pipe, else resolved from checked rows' groups.
    const sameSizeAndWall = (a: any, b: any) =>
      a != null &&
      b != null &&
      Number(a.size) === Number(b.size) &&
      Number(a.wallThickness) === Number(b.wallThickness);
    let sourceSizeWall: any = null;
    if (selectedPipe && !selectedPipe.isAllPipeSizes && selectedPipe.pipeId != null) {
      sourceSizeWall = selectedPipe;
    } else if (selectedRows.length > 0) {
      const entries = selectedRows
        .map((row: any) =>
          (pipeSizesData || []).find(
            (p: any) => String(p?.groupId ?? '').trim() === String(row.groupId ?? '').trim()
          )
        )
        .filter(Boolean);
      const keys = new Set(
        entries.map((p: any) => `${Number(p.size)}|${Number(p.wallThickness)}`)
      );
      if (keys.size === 1) sourceSizeWall = entries[0];
      else if (selectedRows.length > 0 && entries.length > 0) sourceSizeWall = null;
    }

    return (pipeSizesData || [])
      .filter((p: any) => p?.pipeId != null && String(p?.groupId ?? '').trim() !== '')
      .filter((p: any) => currentPipeId == null || String(p.pipeId) !== String(currentPipeId))
      .filter((p: any) => !sharedGroup || String(p.groupId ?? '').trim() !== sharedGroup)
      // Same pipe size + same wall thickness only — target differs by Group ID alone.
      .filter((p: any) => !sourceSizeWall || sameSizeAndWall(p, sourceSizeWall))
      .map((p: any) => ({
        id: p.pipeId,
        name: p.displayName ?? String(p.pipeId),
        pipe: p,
      }));
  }, [pipeSizesData, selectedPipe, filteredRows, selectedRowKeys]);

  // Clear a target that is no longer offered (e.g. it became the selected pipes' own size).
  useEffect(() => {
    if (moveTargetName && !moveTargetOptions.some((o) => o.name === moveTargetName)) {
      setMoveTargetName('');
    }
  }, [moveTargetName, moveTargetOptions]);

  const selectedMoveTarget = useMemo(
    () => moveTargetOptions.find((o) => o.name === moveTargetName)?.pipe ?? null,
    [moveTargetOptions, moveTargetName]
  );

  const moveTargetDisplayName = useMemo(() => {
    const label = selectedMoveTarget?.displayName ?? moveTargetName;
    const trimmed = String(label ?? '').trim();
    return trimmed || undefined;
  }, [selectedMoveTarget, moveTargetName]);

  /** True when every selected pipe already lives in the target group. */
  const moveAlreadyOnTarget = useMemo(() => {
    if (!selectedMoveTarget || selectedPipeDetails.length === 0) return false;
    const targetGroup = String(selectedMoveTarget.groupId ?? '').trim();
    if (!targetGroup) return false;
    const selectedRows = filteredRows.filter((row: any) =>
      selectedRowKeys.some((key) => String(key) === String(row.id))
    );
    return (
      selectedRows.length > 0 &&
      selectedRows.every((row: any) => String(row.groupId ?? '').trim() === targetGroup)
    );
  }, [selectedMoveTarget, selectedPipeDetails, filteredRows, selectedRowKeys]);

  const handleMoveConfirm = async () => {
    if (!selectedMoveTarget || selectedPipeDetails.length === 0) return;
    if (!projectNumber) {
      customNotification.error('Project number is missing. Select a project and try again.');
      return;
    }
    const pipeStatusIds = selectedPipeDetails
      .map((p: any) => Number(p.id))
      .filter((n: number) => Number.isFinite(n) && n > 0);
    if (pipeStatusIds.length === 0) {
      customNotification.error('No valid pipes selected to move.');
      return;
    }

    setIsMoving(true);
    try {
      const result = await movePipesToPipeSize({
        projectNumber: String(projectNumber),
        pipeStatusIds,
        targetPipeId: Number(selectedMoveTarget.pipeId),
        targetGroupId: String(selectedMoveTarget.groupId ?? '').trim(),
        // OD / wall thickness are NOT updated on move — same size + wall, Group ID only.
      });
      const failed = (result.results || []).filter((r) => !r.success);
      const succeeded = (result.results || []).length - failed.length;

      if (failed.length > 0 && succeeded === 0) {
        // e.g. same pipe end ID already available in target group — not allowed, no version created.
        setIsMoveDialogOpen(false);
        customNotification.error(
          'Move not allowed',
          failed.map((r) => r.error).filter(Boolean).join('; ') || 'Failed to move pipe(s)'
        );
        return; // keep selection for retry with a different target
      }
      if (failed.length > 0) {
        customNotification.warning(
          `Moved ${succeeded} of ${result.results.length} pipe(s)`,
          failed.map((r) => r.error).filter(Boolean).join('; ')
        );
      } else {
        customNotification.success(
          `Moved ${succeeded} pipe${succeeded !== 1 ? 's' : ''} to ${selectedMoveTarget.displayName ?? moveTargetName}`
        );
      }
      // Audit: capture group before → after for every successfully moved pipe.
      logPipesMovedToPipeSize({
        projectId:
          project?.project?.projectId != null ? Number(project.project.projectId) : null,
        projectName: project?.project?.projectName ?? null,
        projectNumber: projectNumber ?? null,
        moves: (result.results || [])
          .filter((r) => r.success)
          .map((r) => {
            const detail = selectedPipeDetails.find(
              (p: any) =>
                Number(p.id) === Number(r.sourcePipeStatusId ?? r.pipeStatusId)
            );
            return {
              pipeID: r.pipeID || (detail as any)?.pipeID || '',
              pipeStatusId: r.sourcePipeStatusId ?? r.pipeStatusId,
              beforeGroupId: r.oldGroupID || '',
              afterGroupId:
                r.newGroupID || String(selectedMoveTarget.groupId ?? '').trim(),
              beforePipeId: selectedPipe?.pipeId ?? null,
              afterPipeId: Number(selectedMoveTarget.pipeId),
              mergedIntoPipeStatusId: r.mergedIntoPipeStatusId ?? null,
              sourceDeleted: Boolean(r.sourceDeleted),
              s3: r.s3 ?? null,
            };
          }),
      });

      setIsMoveDialogOpen(false);
      setMoveTargetName('');
      setSelectedRowKeys([]);
      refetchPipeStatus();
    } catch (error: any) {
      customNotification.error(error?.message || 'Failed to move pipe(s) to target pipe size');
    } finally {
      setIsMoving(false);
    }
  };

  const isAllSelected =
    filteredRows.length > 0 && filteredRows.every((row: any) => selectedRowKeys.some((key) => String(key) === String(row.id)));
  const isSomeSelected =
    filteredRows.some((row: any) => selectedRowKeys.some((key) => String(key) === String(row.id))) && !isAllSelected;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRowKeys(filteredRows.map((row: any) => row.id));
    } else {
      setSelectedRowKeys([]);
    }
  };

  const handleClearSelection = () => setSelectedRowKeys([]);
  const handleRequestDelete = () => {
    // Toolbar delete uses currently checked rows only.
    setPipesPendingDelete(selectedPipeDetails);
    const hasA = selectedPipeDetails.some((p: any) => Boolean(p.endA || p.pipeEndIDA));
    const hasB = selectedPipeDetails.some((p: any) => Boolean(p.endB || p.pipeEndIDB));
    setDeleteEndA(hasA);
    setDeleteEndB(hasB);
    setDeleteConfirmOpen(true);
  };
  const handleRequestMarkArchive = () => {
    setArchiveEditPipes(
      filteredRows
        .filter((row: any) => selectedRowKeys.some((key) => String(key) === String(row.id)))
        .map(toFlagPipeDetail)
    );
    setIsPipeArchiveEditOpen(true);
  };
  const handleRequestMarkFlange = () => {
    setFlangeEditPipes(
      filteredRows
        .filter((row: any) => selectedRowKeys.some((key) => String(key) === String(row.id)))
        .map(toFlagPipeDetail)
    );
    setIsPipeFlangeEditOpen(true);
  };
  const handleRequestMarkAnode = () => {
    setAnodeEditPipes(
      filteredRows
        .filter((row: any) => selectedRowKeys.some((key) => String(key) === String(row.id)))
        .map(toFlagPipeDetail)
    );
    setIsPipeAnodeEditOpen(true);
  };
  const handleRequestMovePipe = () => {
    setMoveTargetName('');
    setIsMoveDialogOpen(true);
  };
  const moveDialogPipes = useMemo(
    () =>
      selectedPipeDetails.map((p: any) => ({
        id: p.id,
        pipeID: String(p.pipeID ?? ''),
      })),
    [selectedPipeDetails]
  );
  const pipesToDeleteDetails = pipesPendingDelete;
  const deleteDialogHasEndA = pipesToDeleteDetails.some(
    (p: any) => Boolean(p.endA || p.pipeEndIDA)
  );
  const deleteDialogHasEndB = pipesToDeleteDetails.some(
    (p: any) => Boolean(p.endB || p.pipeEndIDB)
  );
  const handleDeleteConfirmCancel = () => {
    if (!isDeleting) {
      setDeleteConfirmOpen(false);
      setPipesPendingDelete([]);
      setDeleteEndA(true);
      setDeleteEndB(true);
    }
  };
  const handleDeleteConfirmOk = async () => {
    const ends: PipeEndKey[] = [];
    if (deleteEndA) ends.push('EndA');
    if (deleteEndB) ends.push('EndB');
    if (ends.length === 0) {
      customNotification.error('Select ends', 'Choose End A, End B, or both to delete.');
      return;
    }
    if (!projectNumber || pipesToDeleteDetails.length === 0) return;

    setIsDeleting(true);
    try {
      let anySoftDeleted = false;
      for (const pipe of pipesToDeleteDetails) {
        const statusId = Number(pipe.id);
        if (!Number.isFinite(statusId) || statusId <= 0) continue;
        const result = await deletePipeEnds({
          id: statusId,
          projectNumber: String(projectNumber),
          ends,
        });
        logPipeEndsDeleted({
          projectId: project?.project?.projectId != null ? Number(project.project.projectId) : null,
          projectName: project?.project?.projectName ?? null,
          projectNumber: String(projectNumber),
          statusId,
          pipeID: String(pipe.pipeID || ''),
          ends,
          softDeletedStatus: result.softDeletedStatus,
        });
        if (result.softDeletedStatus) anySoftDeleted = true;
      }
      customNotification.success(
        anySoftDeleted ? 'Pipe ends deleted (status removed where both ends cleared)' : 'Pipe ends deleted'
      );
      setSelectedRowKeys([]);
      setPipesPendingDelete([]);
      setDeleteConfirmOpen(false);
      setDeleteEndA(true);
      setDeleteEndB(true);
      refetchPipeStatus();
    } catch (error: any) {
      customNotification.error(
        'Delete failed',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditModalClose = () => {
    if (!isSavingEdit) {
      setIsEditModalOpen(false);
      setPipeToEdit(null);
    }
  };
  const handleSaveEdit = () => {
    if (!pipeToEdit?.id) return;
    setIsSavingEdit(true);
    const statusValue = editStatus === 'Default State' ? 'defaultState' : editStatus;
    dispatch(
      pipefitterUpdatePipeStatus(
        { id: pipeToEdit.id, status: statusValue },
        () => {
          // Only refetch and close modal after update succeeds (callback runs only on success)
          refetchPipeStatus();
          setIsEditModalOpen(false);
          setPipeToEdit(null);
          setIsSavingEdit(false);
        },
        () => setIsSavingEdit(false)
      )
    );
  };

  if (isReportsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]" />
          <span className="text-gray-600 text-lg">Loading pipe data...</span>
        </div>
      </div>
    );
  }

  // Do not use totalCount === 0 alone: some APIs send 0 while still returning rows; empty
  // current page with totalCount > 0 should show the table shell, not full-screen "No data".
  if (pipeStatusFetchComplete && pipeStatusData.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <span className="text-gray-600 text-lg">No data available.</span>
      </div>
    );
  }

  const filteredTotal = filteredRows.length;
  const paginatePipeTable = filteredTotal > commonConstants.clientPaginationThreshold;
  const rangeStart = displayData.length === 0 ? 0 : paginatePipeTable ? pipeStatusSkip + 1 : 1;
  const rangeEnd = paginatePipeTable ? pipeStatusSkip + displayData.length : filteredTotal;
  const serverTotal =
    pipeStatusTotalCount != null && pipeStatusTotalCount > 0 ? pipeStatusTotalCount : null;
  // Prefer fetchComplete — loadingMore can briefly flicker false between batches.
  const isBackgroundLoading =
    !pipeStatusFetchComplete &&
    (pipeStatusLoadingMore ||
      (serverTotal != null && pipeStatusData.length > 0 && pipeStatusData.length < serverTotal));
  const loadPercent =
    isBackgroundLoading && serverTotal != null
      ? Math.min(100, Math.round((pipeStatusData.length / serverTotal) * 100))
      : null;
  const loadProgressSuffix =
    loadPercent != null
      ? `(${loadPercent}% loaded)`
      : isBackgroundLoading
        ? '(loading more…)'
        : '';
  // While background fetch continues, show server total so "of N" stays stable (e.g. of 1772).
  const rangeOfTotal =
    isBackgroundLoading && !hasActiveFilters && serverTotal != null
      ? serverTotal
      : filteredTotal;
  // Paginate only loaded rows — do not invent empty pages for not-yet-fetched data.
  const paginationTotalItems = filteredTotal;

  return (
    <>
      <div className="space-y-4">
        <PipeStatusFilters
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filters={filters}
          onFilterChange={onFilterChange}
          onEndPresenceChange={handleEndPresenceChange}
          onArchiveChange={handleArchiveChange}
          onPipeMarkChange={handlePipeMarkChange}
          onDuplicatesChange={handleDuplicatesChange}
          uniquePipeIDs={uniquePipeIDs}
          uniqueGroupIds={uniqueGroupIds}
          showGroupIdFilter={isAllPipeSizes}
          hasActiveFilters={hasActiveFilters}
          filteredCount={toolbarPipeCount}
          onResetFilters={handleResetFilters}
        />

        {selectedRowKeys.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
            <span className="text-[12px] text-text-grey">
              {selectedRowKeys.length} pipe{selectedRowKeys.length !== 1 ? 's' : ''} selected
            </span>
            <Button variant="outline" size="sm" onClick={handleClearSelection} className="h-8 text-[12px]">
              <X className="h-3.5 w-3.5 mr-1" />
              Clear
            </Button>
            {canEdit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRequestDelete}
                  className="h-8 text-[12px] text-readable-red border-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Delete
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRequestMarkArchive}
                 className="h-8 text-[12px] text-readable-red border-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                >
                  <Archive className="h-3.5 w-3.5 mr-1" />
                  Mark as Archived
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRequestMarkFlange}
                  className="h-8 text-[12px] text-readable-red border-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                >
                  <Disc className="h-3.5 w-3.5 mr-1" />
                  Mark as Flange
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRequestMarkAnode}
                  className="h-8 text-[12px] text-readable-red border-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                >
                  <Zap className="h-3.5 w-3.5 mr-1" />
                  Mark as Anode
                </Button>
                {moveTargetOptions.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRequestMovePipe}
                    disabled={isMoving}
                    className="h-8 text-[12px] text-readable-red border-readable-red hover:bg-[rgba(196,0,31,0.06)]"
                  >
                    <MoveRight className="h-3.5 w-3.5 mr-1" />
                    Move pipe
                  </Button>
                )}
              </>
            )}
          </div>
        )}

        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-[12px] text-heritage-grey">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              {filteredTotal === 0
                ? searchQuery.trim()
                  ? `No pipe "${searchQuery.trim()}" found in loaded data`
                  : 'No pipes match the current filters'
                : `Showing ${rangeStart}–${rangeEnd} of ${rangeOfTotal}`}
              {loadProgressSuffix ? (
                <span className="ml-1 inline-flex items-center gap-1.5 font-semibold text-blueprint-blue">
                  <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
                  {loadProgressSuffix}
                </span>
              ) : null}
            </span>
          </span>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:justify-end">
      
            <label className="inline-flex items-center gap-2 cursor-pointer select-none text-text-grey">
              <Checkbox
                checked={includeArchivedPipes}
                onCheckedChange={(checked) => handleIncludeArchivedChange(checked === true)}
                aria-label="Include archived pipes"
              />
              <span>Include archived pipes</span>
            </label>
            <span>
              Both:{' '}
              <span className="font-semibold text-text-grey">
                {endPresenceCounts.both}
                {pipeEndPresenceFlagSuffix(
                  endPresenceFlagCounts.bothFlange,
                  endPresenceFlagCounts.bothArchive,
                  endPresenceFlagCounts.bothAnode
                )}
              </span>
            </span>
            <span aria-hidden className="text-[#E2E5EA]">
              —
            </span>
            <span>
              Only End A:{' '}
              <span className="font-semibold text-text-grey">
                {endPresenceCounts.endAOnly}
                {pipeEndPresenceFlagSuffix(
                  endPresenceFlagCounts.endAOnlyFlange,
                  endPresenceFlagCounts.endAOnlyArchive,
                  endPresenceFlagCounts.endAOnlyAnode
                )}
              </span>
            </span>
            <span aria-hidden className="text-[#E2E5EA]">
              —
            </span>
            <span>
              Only End B:{' '}
              <span className="font-semibold text-text-grey">
                {endPresenceCounts.endBOnly}
                {pipeEndPresenceFlagSuffix(
                  endPresenceFlagCounts.endBOnlyFlange,
                  endPresenceFlagCounts.endBOnlyArchive,
                  endPresenceFlagCounts.endBOnlyAnode
                )}
              </span>
            </span>
          </span>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="overflow-auto max-h-[400px] md:max-h-[500px] lg:max-h-[calc(100vh-350px)]">
              <Table className="w-full table-fixed" containerClassName="w-full min-w-0 overflow-visible">
                <colgroup>
                  <col style={{ width: '5%' }} />
                  {columns.map((col: any) => (
                    <col key={col.dataIndex} style={{ width: col.colWidth }} />
                  ))}
                </colgroup>
                <TableHeader className="sticky top-0 z-20 bg-white">
                    <TableRow>
                      <TableHead className="w-12 text-center pl-3 pr-2">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAllSelected}
                            ref={(el) => {
                              if (el) {
                                (el as any).indeterminate = isSomeSelected;
                              }
                            }}
                            onCheckedChange={(checked) => handleSelectAll(checked === true)}
                            className="cursor-pointer"
                            title={isAllSelected ? 'Deselect all' : 'Select all'}
                            aria-label={isAllSelected ? 'Deselect all' : 'Select all'}
                          />
                        </div>
                      </TableHead>
                      {columns.map((col: any, idx: number) => (
                        <TableHead key={idx} className={col.headerClassName}>
                          {col.align === 'center' ? (
                            <div className="flex justify-center">{col.title}</div>
                          ) : (
                            col.title
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length + 1}
                          className="py-10 text-center text-[13px] text-heritage-grey"
                        >
                          {searchQuery.trim() || hasActiveFilters
                            ? 'No pipes match your search or filters.'
                            : 'No pipes on this page.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayData.map((row: any, idx: number) => (
                    <TableRow key={row.id ?? row.pipeID ?? idx}>
                      <TableCell className="w-12 text-center pl-3 pr-2">
                        <Checkbox
                          checked={selectedRowKeys.some((key) => String(key) === String(row.id))}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedRowKeys([...selectedRowKeys, row.id]);
                            } else {
                              setSelectedRowKeys(selectedRowKeys.filter((key) => String(key) !== String(row.id)));
                            }
                          }}
                          aria-label={`Select pipe ${row.pipeID}`}
                        />
                      </TableCell>
                      {columns.map((col: any, colIdx: number) => (
                        <TableCell
                          key={colIdx}
                          className={
                            col.cellClassName ??
                            (col.align === 'center' ? 'text-center align-middle' : undefined)
                          }
                        >
                          {col.render
                            ? col.render(row[col.dataIndex], row)
                            : row[col.dataIndex]}
                        </TableCell>
                      ))}
                    </TableRow>
                      ))
                    )}
                </TableBody>
              </Table>
            </div>
            {filteredTotal > commonConstants.clientPaginationThreshold ? (
              <Pagination
                currentPage={clientPipeStatusPage}
                totalItems={paginationTotalItems}
                displayTotalItems={
                  isBackgroundLoading && !hasActiveFilters && serverTotal != null
                    ? serverTotal
                    : undefined
                }
                itemsPerPage={pipeStatusLimit}
                onPageChange={(page) => {
                  const lim = clampPipeStatusPageSize(pipeStatusLimitRef.current);
                  setClientPagination((page - 1) * lim, lim);
                }}
                onItemsPerPageChange={(n) => {
                  const lim = clampPipeStatusPageSize(n);
                  pipeStatusLimitRef.current = lim;
                  setClientPagination(0, lim);
                }}
                pageSizeOptions={[25, 50, 100, 200, 500]}
                showPageNumbers
                maxVisiblePages={5}
                rangeSuffix={loadProgressSuffix || undefined}
              />
            ) : null}
          </CardContent>
        </Card>
      </div>


      {selectedPipe && (
        <PipeFfitter
          isOpen={isPipeFitterOpen}
          isPipeFitterOpen={isPipeFitterOpen}
          onClose={() => {
            setIsPipeFitterOpen(false);
            setPipeFitterRow(null);
          }}
          defaultEnd={pipeFitterDefaultEnd}
          pipeStatusRow={pipeFitterRow}
          selectedMaterial={undefined}
          selectedPipe={selectedPipe}
          project={project}
          unit="mm"
        />
      )}


      {/* <Dialog open={modalVisible} onOpenChange={setModalVisible}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Sequence</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Sequence</Label>
              <Select value={selectedSequence || undefined} onValueChange={setSelectedSequence}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Sequence" />
                </SelectTrigger>
                <SelectContent>
                  {configurationList.map((spread: any) => (
                    <SelectItem key={spread.spreadName} value={spread.spreadName}>
                      {spread.spreadName}
                    </SelectItem>
                  ))}
                  <SelectItem value={configurationConstants.importDataSpread.anySpread}>
                    Any
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleModalCancel}>
                Cancel
              </Button>
              <Button onClick={handleModalOk} disabled={!selectedSequence}>
                Assign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isTableModalVisible} onOpenChange={setIsTableModalVisible}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Pipe Data View</DialogTitle>
          </DialogHeader>
          <PipeDataView
            columns={columns}
            onClose={handleTableModalCancel}
            filteredData={filteredData}
          />
        </DialogContent>
      </Dialog> */}

      {/* Edit pipe / status modal */}
      <Dialog open={isEditModalOpen} onOpenChange={(open) => !open && handleEditModalClose()}>
        <DialogContent className="max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
           Update Pipe Status
            </DialogTitle>
          </DialogHeader>
          {pipeToEdit && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-gray-600">Pipe ID: <span className="font-medium text-gray-900">{pipeToEdit.pipeID}</span></p>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={editStatus || (pipeToEdit.status === 'defaultState' ? 'Default State' : pipeToEdit.status) || ''}
                  onValueChange={setEditStatus}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {PIPE_STATUS_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={handleEditModalClose} disabled={isSavingEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSavingEdit}>
              {isSavingEdit ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={(open) => {
          if (!open && !isDeleting) {
            setDeleteConfirmOpen(false);
            setPipesPendingDelete([]);
            setDeleteEndA(true);
            setDeleteEndB(true);
          }
        }}
      >
        <DialogContent className="max-w-[800px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle className="text-xl font-semibold">
              Delete ends
            </DialogTitle>
          </div>
          <div className="mb-6 space-y-4">
            {isDeleting && (
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md">
                <Loader2 className="h-5 w-5 animate-spin text-red-600" />
                <span className="text-sm text-gray-600">Deleting and refreshing...</span>
              </div>
            )}
            <p className="text-gray-700">
              Remove End A, End B, or both. Deleted ends clear on pipe status and
              mark matching pipeinfo rows as deleted (pipeEndID unchanged).
              Deleting both ends (or the last remaining end) removes the status row.
            </p>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600 mb-2">Selected pipe details:</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {pipesToDeleteDetails.map((p) => (
                  <div key={p.id ?? p.pipeID} className="text-sm text-gray-700 py-1 flex items-center">
                    <span className="w-2 h-2 bg-gray-400 rounded-full mr-2 flex-shrink-0" />
                    Pipe ID: {p.pipeID}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-[#E2E5EA] bg-page-bg/40 p-4 space-y-3">
              <div className="flex flex-wrap gap-4">
                {deleteDialogHasEndA ? (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      checked={deleteEndA}
                      disabled={isDeleting}
                      onCheckedChange={(v) => setDeleteEndA(v === true)}
                    />
                    <span className="text-[13px] text-gray-900">End A</span>
                  </label>
                ) : null}
                {deleteDialogHasEndB ? (
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      checked={deleteEndB}
                      disabled={isDeleting}
                      onCheckedChange={(v) => setDeleteEndB(v === true)}
                    />
                    <span className="text-[13px] text-gray-900">End B</span>
                  </label>
                ) : null}
                {!deleteDialogHasEndA && !deleteDialogHasEndB ? (
                  <p className="text-[13px] text-heritage-grey">No ends available to delete.</p>
                ) : null}
              </div>
            </div>

            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                <span className="font-semibold">Warning:</span> This action is irreversible.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={handleDeleteConfirmCancel}
              disabled={isDeleting}
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirmOk}
              disabled={isDeleting || (!deleteEndA && !deleteEndB)}
              className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
            >
              {isDeleting ? 'Deleting...' : 'Delete selected ends'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MovePipesDialog
        open={isMoveDialogOpen}
        onOpenChange={(open) => {
          setIsMoveDialogOpen(open);
          if (!open && !isMoving) setMoveTargetName('');
        }}
        pipes={moveDialogPipes}
        targetOptions={moveTargetOptions}
        targetName={moveTargetName}
        onTargetNameChange={setMoveTargetName}
        targetDisplayName={moveTargetDisplayName}
        moveAlreadyOnTarget={moveAlreadyOnTarget}
        isMoving={isMoving}
        onConfirm={handleMoveConfirm}
      />

      <UpdatePipeIdDialog
        open={isPipeIdEditOpen}
        onOpenChange={(open) => {
          setIsPipeIdEditOpen(open);
          if (!open) setPipeIdEditRow(null);
        }}
        pipe={pipeIdEditRow}
        auditContext={{
          projectId: project?.project?.projectId != null ? Number(project.project.projectId) : null,
          projectName: project?.project?.projectName ?? null,
          projectNumber: projectNumber ?? null,
        }}
        onRenamed={() => {
          refetchPipeStatus();
        }}
      />

      <UpdatePipeFlagsDialog
        open={isPipeArchiveEditOpen}
        onOpenChange={(open) => {
          setIsPipeArchiveEditOpen(open);
          if (!open) setArchiveEditPipes([]);
        }}
        pipes={archiveEditPipes}
        mode="archive"
        onUpdated={() => {
          refetchPipeStatus();
          setSelectedRowKeys([]);
        }}
      />

      <UpdatePipeFlagsDialog
        open={isPipeFlangeEditOpen}
        onOpenChange={(open) => {
          setIsPipeFlangeEditOpen(open);
          if (!open) setFlangeEditPipes([]);
        }}
        pipes={flangeEditPipes}
        mode="flange"
        onUpdated={() => {
          refetchPipeStatus();
          setSelectedRowKeys([]);
        }}
      />

      <UpdatePipeFlagsDialog
        open={isPipeAnodeEditOpen}
        onOpenChange={(open) => {
          setIsPipeAnodeEditOpen(open);
          if (!open) setAnodeEditPipes([]);
        }}
        pipes={anodeEditPipes}
        mode="anode"
        onUpdated={() => {
          refetchPipeStatus();
          setSelectedRowKeys([]);
        }}
      />
    </>
  );
};
